import React, { useState, useEffect, useRef } from "react";
import { useAppSelector, useAppDispatch } from "../../../hooks/hooks";
import {
  loginAsync,
  selectAuthStatus,
  selectUsername,
  selectRememberUsername,
  saveUsername,
  getMemberExperienceDataAsync,
  logoutAsync,
} from "../../../features/authentication/authenticationSlice";
import { selectBrandInfo } from "../../../features/brand/brandSlice";
import { getSubscriptionDetailsAsync } from "../../../features/subscriptions/subscriptionSlice";

import "./login.css";

import {
  getAllListsAsync,
  getAllReportsWithNotesAsync,
  getReportHistoryAsync,
} from "../../../features/reports/reportsSlice";

import {
  Container,
  Padding,
  Layout,
  Text,
  Sheet,
  Input,
  Button,
  AnchorLink,
  CheckBox,
  Form,
  Header,
} from "@kidslivesafe/sc-react";
import { useNavigate, useLocation } from "react-router-dom";

import Logo from "@assets/images/brand-logo.svg";
import HeaderLogo from "@assets/images/brand-logo--header.svg";

import {
  getMonitoringAsync,
  getNeighborhoodWatchMonitoringAreasAsync,
  getPropertyMonitoringAsync,
} from "../../../features/monitoring/monitoringSlice";
import { fetchCustomerBrokersAsync } from "../../../features/profileProtection/profileProtectionSlice";
import { useCurrentBreakpoint } from "@kidslivesafe/sc-foundation";
import { formatPhoneNumber } from "../../../utils";
import TurnstileCaptcha, {
  TurnstileCaptchaRef,
} from "../../../components/UtilComponents/TurnsileCaptcha";
import {
  getShowCaptchaAsync,
  selectEnableLoginCaptcha,
  selectRateLimitError,
} from "../../../features/admin/adminSlice";

const Login: React.FC = () => {
  /* -- local state -- */
  const [email, setEmail] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [emailError, setEmailError] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<boolean>(false);
  const [password, setPassword] = useState<string>("");
  const [localRememberUsername, setLocalRememberUsername] =
    useState<boolean>(false);
  const [turnstileError, setTurnstileError] = useState<boolean>(false);
  const [turnstileErrorMessage, setTurnstileErrorMessage] =
    useState<string>("");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  /* -- redux state -- */
  const authStatus = useAppSelector(selectAuthStatus);
  const brandInfo = useAppSelector(selectBrandInfo);
  const username = useAppSelector(selectUsername);
  const rememberUsername = useAppSelector(selectRememberUsername);
  const enableLoginCaptcha = useAppSelector(selectEnableLoginCaptcha);
  const rateLimitError = useAppSelector(selectRateLimitError);

  /* -- setup hooks -- */
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const currentBreakPoint = useCurrentBreakpoint();
  const turnstileRef = useRef<TurnstileCaptchaRef>(null);

  useEffect(() => {
    const myCookie = getCookie("username");
    const emailToSet = username || myCookie;

    if (emailToSet) {
      setEmail(emailToSet);
    }
    setLocalRememberUsername(rememberUsername);
  }, [username, rememberUsername]);

  useEffect(() => {
    if (rateLimitError) {
      setErrorMessage(
        "Account locked. Please contact customer support at " +
          formatPhoneNumber(brandInfo.phoneNumber),
      );
      setEmailError(true);
      setPasswordError(true);
    } else {
      setErrorMessage("");
      setEmailError(false);
      setPasswordError(false);
    }
  }, [rateLimitError]);

  useEffect(() => {
    // Get Show Captcha Flag
    dispatch(getShowCaptchaAsync());
  }, []);

  const getCookie = (name: string) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(";").shift();
    }
  };

  // Check for redirect query parameter
  const redirect = new URLSearchParams(location.search).get("redirect");

  /* -- handle submit -- */
  const handleLoginSubmit = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
  ) => {
    if (e) {
      e.preventDefault();
    }
    try {
      if (!email && !password) {
        setErrorMessage("Please fill out the entire form.");
        setEmailError(true);
        setPasswordError(true);
        return;
      } else if (!password) {
        setErrorMessage("Please fill out the entire form.");
        setPasswordError(true);
        return;
      } else if (!email) {
        setErrorMessage("Please fill out the entire form.");
        setEmailError(true);
        return;
      } else if (!turnstileToken && enableLoginCaptcha === true) {
        setTurnstileError(true);
        setTurnstileErrorMessage("Please complete CAPTCHA first.");
        return;
      }

      const incompleteLogin = async (reason: string) => {
        setPassword("");
        setPasswordError(true);
        setErrorMessage("Login failed. Please try again.");
        await dispatch(logoutAsync({ location: `login.${reason}.rejected` }));
      };

      const resultAction = await dispatch(
        loginAsync({ email, password, token: turnstileToken }),
      );
      if (loginAsync.fulfilled.match(resultAction)) {
        const subscriptionAction = await dispatch(
          getSubscriptionDetailsAsync(),
        );
        if (getSubscriptionDetailsAsync.rejected.match(subscriptionAction)) {
          await incompleteLogin("getSubscriptionDetails");
          return;
        }

        dispatch(saveUsername(localRememberUsername));

        const memberExperienceAction = await dispatch(
          getMemberExperienceDataAsync(),
        );
        if (
          getMemberExperienceDataAsync.rejected.match(memberExperienceAction)
        ) {
          await incompleteLogin("getMemberExperienceData");
          return;
        }

        const memberExperienceVariant =
          memberExperienceAction.payload?.memberExperienceData?.result?.contents
            .variant;
        if (memberExperienceVariant === "pis75") {
          dispatch(fetchCustomerBrokersAsync());
        }

        // get report history
        dispatch(getReportHistoryAsync());

        // trigger get monitoring
        dispatch(getMonitoringAsync());
        dispatch(getNeighborhoodWatchMonitoringAreasAsync());
        dispatch(getPropertyMonitoringAsync());

        // get lists data
        dispatch(getAllListsAsync());

        // get notes data
        dispatch(getAllReportsWithNotesAsync());
        console.log("Login successful");

        if (getSubscriptionDetailsAsync.fulfilled.match(subscriptionAction)) {
          const { pauseDetails } = subscriptionAction.payload;

          if (pauseDetails && pauseDetails.isCurrentlyPaused) {
            setTimeout(() => {
              navigate(redirect || "/account-paused");
            }, 0);
          } else {
            setTimeout(() => {
              navigate(redirect || "/");
            }, 0);
          }
        }
      } else {
        if (resultAction.error.message === "HasCustomerLockout") {
          setErrorMessage(
            "Account locked. Please contact customer support at " +
              formatPhoneNumber(brandInfo.phoneNumber),
          );
        } else {
          setErrorMessage("Login failed. Please try again.");
        }
        setEmailError(true);
        setPasswordError(true);
      }
    } catch (error) {
      console.error("Login failed:", error);
    } finally {
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
    }
  };

  const handleRememberUsername = (value: boolean) => {
    setLocalRememberUsername(value);
  };

  const renderLogo = () => {
    switch (brandInfo.acronym) {
      case "sf":
        return (
          <img
            width={currentBreakPoint === "desktop" ? "147" : "88"}
            src={Logo}
            alt="Logo"
          />
        );
      case "pdc":
        return (
          <img
            width={currentBreakPoint === "desktop" ? "170" : "170"}
            src={Logo}
            alt="Logo"
          />
        );
      case "pis":
        return (
          <img
            width={currentBreakPoint === "desktop" ? "170" : "170"}
            src={HeaderLogo}
            alt="Logo"
          />
        );
      case "spr":
        return (
          <img
            width={currentBreakPoint === "desktop" ? "170" : "170"}
            src={HeaderLogo}
            alt="Logo"
          />
        );
      case "prr":
        return <img width="147" src={HeaderLogo} alt="Logo" />;
      case "kls":
        return (
          <img
            width={currentBreakPoint === "desktop" ? "170" : "170"}
            src={Logo}
            alt="Logo"
          />
        );
      case "qpr":
        return (
          <img
            width={currentBreakPoint === "desktop" ? "170" : "170"}
            src={Logo}
            alt="Logo"
          />
        );
      case "pf":
        return (
          <img
            width={currentBreakPoint === "desktop" ? "170" : "170"}
            src={HeaderLogo}
            alt="Logo"
          />
        );
      default:
        return (
          <img
            width={currentBreakPoint === "desktop" ? "147" : "88"}
            src={Logo}
            alt="Logo"
          />
        );
    }
  };

  return (
    <>
      {brandInfo.acronym === "qpr" ? (
        <Header
          homeUrl="/"
          displayButtons={false}
          menuButtonLinks={[]}
          logoUrl={HeaderLogo}
        ></Header>
      ) : (
        <Sheet>
          <Layout
            type="flex"
            flexProps={{ direction: "column", alignItems: "flex-start" }}
          >
            <Padding space="md">
              <div className="page-login-brand-logo-container">
                <Text color="white">{renderLogo()}</Text>
              </div>
            </Padding>
          </Layout>
        </Sheet>
      )}
      <Sheet>
        <Padding space="md">
          <div
            className={
              currentBreakPoint === "desktop"
                ? "page-login-sheet-form-container"
                : ""
            }
          >
            <Layout type="flex" flexProps={{ direction: "column", gap: "lg" }}>
              <Container
                bodyBorderRadius
                border={brandInfo.acronym === "qpr" ? true : false}
                customClassName="page-login__container"
                altColor="login"
              >
                <Padding
                  space={["pf"].includes(brandInfo.acronym) ? "xl" : "md"}
                >
                  <Form>
                    <Layout
                      type="flex"
                      flexProps={{
                        direction: "column",
                        gap: "md",
                      }}
                    >
                      <Layout
                        type="flex"
                        flexProps={{
                          direction: "column",
                          gap: "xl",
                        }}
                      >
                        <Text tag="h1">Member Login</Text>
                        {["qpr"].includes(brandInfo.acronym) ? null : (
                          <Text size="xs" fontWeight="semibold">
                            Don't have an account?{" "}
                            <AnchorLink
                              inline
                              color="primary"
                              href={brandInfo.homeUrl}
                              size="xs"
                              fontWeight={
                                ["qpr", "pf"].includes(brandInfo.acronym)
                                  ? "normal"
                                  : "semibold"
                              }
                            >
                              Join Today!
                            </AnchorLink>
                          </Text>
                        )}
                      </Layout>
                      <Layout
                        type="flex"
                        flexProps={{
                          direction: "column",
                          gap: "xs",
                        }}
                      >
                        <Input
                          id="login-email"
                          type="email"
                          value={email}
                          handleChange={setEmail}
                          placeholder="Enter Email"
                          handleError={() => {
                            setEmailError(false);
                            setPasswordError(false);
                          }}
                          error={emailError}
                          enableLastPass
                        />
                        <Input
                          id="login-password"
                          type="password"
                          placeholder="Enter Password"
                          value={password}
                          handleChange={setPassword}
                          handleError={() => {
                            setEmailError(false);
                            setPasswordError(false);
                          }}
                          error={passwordError}
                          enableLastPass
                        />
                        {(emailError || passwordError) && errorMessage && (
                          <Layout
                            type="flex"
                            flexProps={{ alignSelf: "flex-start" }}
                          >
                            <Text size="xs" color="error">
                              {errorMessage}
                            </Text>
                          </Layout>
                        )}
                      </Layout>
                      <Text size="xs" tag="div">
                        <Layout
                          type="flex"
                          flexProps={{ direction: "row", gap: "xxs" }}
                        >
                          <CheckBox
                            labelText="Remember Username"
                            checkedValue={localRememberUsername}
                            handleChange={handleRememberUsername}
                          />
                        </Layout>
                      </Text>
                      {enableLoginCaptcha === true && (
                        <>
                          <TurnstileCaptcha
                            siteKey={brandInfo.turnstileSiteKey}
                            ref={turnstileRef}
                            onVerify={token => {
                              setTurnstileToken(token);
                              setTurnstileError(false);
                            }}
                            onError={error => {
                              setTurnstileErrorMessage(
                                "ERROR - Something went wrong. Try again later.",
                              );
                              setTurnstileToken(null);
                              setTurnstileError(true);
                            }}
                            onExpire={() => {
                              setTurnstileErrorMessage(
                                "ERROR - Captcha token expired. Please refresh and try again.",
                              );
                              setTurnstileToken(null);
                              setTurnstileError(true);
                            }}
                            onUnsupported={() => {
                              setTurnstileErrorMessage(
                                "ERROR - Captcha unsupported by browser.",
                              );
                              setTurnstileToken(null);
                              setTurnstileError(true);
                            }}
                          />
                          {turnstileError && (
                            <Layout
                              type="flex"
                              flexProps={{ alignSelf: "flex-start" }}
                            >
                              <Text size="xs" color="error">
                                {turnstileErrorMessage}
                              </Text>
                            </Layout>
                          )}
                        </>
                      )}
                      <Button
                        buttonText="Log In"
                        color="primary"
                        handleClick={handleLoginSubmit}
                        disabled={authStatus === "loading"}
                        size="md"
                        fill="fill"
                        passThroughClassess="page-loign__button-border"
                      />
                      <Layout
                        type="flex"
                        flexProps={{
                          alignItems: "center",
                          direction: "column",
                          gap: "xl",
                        }}
                      >
                        <Layout
                          type="flex"
                          flexProps={{
                            alignItems: "center",
                            direction: "column",
                            gap: "sm",
                          }}
                        >
                          <AnchorLink
                            href="/reset-password"
                            target="_self"
                            size="xs"
                          >
                            Forgot Password?
                          </AnchorLink>

                          <AnchorLink
                            href="/login-link-request"
                            target="_self"
                            size="xs"
                          >
                            Get One Time Login Link
                          </AnchorLink>
                        </Layout>
                        <Layout
                          type="flex"
                          flexProps={{
                            alignItems: "center",
                            direction: "column",
                            gap: "xxs",
                          }}
                        >
                          <Text size="sm" fontWeight="semibold">
                            Need Help? Contact Us
                          </Text>
                          <AnchorLink
                            inline
                            phoneNumber
                            target="_self"
                            hoverUnderline
                            href={brandInfo.phoneNumber}
                            size="xs"
                          >
                            {formatPhoneNumber(brandInfo.phoneNumber)}
                          </AnchorLink>
                        </Layout>
                      </Layout>
                    </Layout>
                  </Form>
                </Padding>
              </Container>
            </Layout>
          </div>
        </Padding>
      </Sheet>
    </>
  );
};

export default Login;
